// 브라우저 환경에서 Buffer 사용

import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Image } from '@codes/ui-kit/components/ui/image';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { CatalogView, ProductOrderBody, ProductView } from '@lemoncloud/codes-goods-api';
import { MediaView } from '@lemoncloud/codes-goods-api/dist/modules/medias/views';
import { useProjectService, useWorkspaceService } from '@web/app/features/home/hooks';
import { useSelectedStore, useStereoIcon, useUpdateQueryData } from '@web/app/shared';
import { AxiosError } from 'axios';
import { ChevronRight, File, X } from 'lucide-react';

import { GAEvents, trackEvent } from '@codes/analytics';
import { CompleteImage } from '@codes/assets';
import { useUploadFiles } from '@codes/files';
import { PaginationType, productsKey, useOrderCatalogs } from '@codes/product';
import { ImageItem, PdfItem, getCurrencySymbol } from '@codes/react-utils';
import { PrivacyTermsPage } from '@codes/shared';
import {
    Avatar,
    AvatarImage,
    Badge,
    Button,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
    Input,
    Label,
    SharedSpinner,
    Spinner,
} from '@codes/ui-kit';
import { formatDate } from '@codes/utils';

interface OrderProductDialogProps {
    catalog: CatalogView;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => any;
}

export const OrderProductDialog = ({ catalog, isOpen, onOpenChange }: OrderProductDialogProps) => {
    const getStereoIcon = useStereoIcon();

    const { project } = useSelectedStore();

    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const updateProductQueryData = useUpdateQueryData<PaginationType<ProductView[]>>();

    const [agree, setAgree] = useState(false);
    const scrollDivRef = useRef<HTMLDivElement>(null);

    const productOrderForm = useForm<ProductOrderBody>({
        defaultValues: {
            memo: '',
            medias: [],
        },
    });

    const [watchedMemo] = useWatch({
        control: productOrderForm.control,
        name: ['memo'],
    });

    const [medias, setMedias] = useState<MediaView[]>([]);
    const [isOrdered, setIsOrdered] = useState(false);
    const [orderedProduct, setOrderedProduct] = useState<ProductView | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);
    const {
        mutateAsync: uploadFiles,
        isPending: isUploading,
        data: uploadedFilesData,
        reset: resetUploadedFiles,
    } = useUploadFiles();

    const isFileBtnDisabled = isUploading || medias.length >= 5;

    const { workspaceFromServer } = useWorkspaceService();
    const { myProject } = useProjectService();

    const closeDialog = () => {
        onOpenChange(false);
        if (!workspaceFromServer || !myProject) {
            toast({
                description: 'Unable to access workspace or project. Please check your permissions and try again.',
                variant: 'destructive',
            });
            navigate('/w', { replace: true });
            return;
        }

        navigate(`/w/${workspaceFromServer?.id}/p/${myProject.id}`, { replace: true });
    };

    const orderCatalogs = useOrderCatalogs(
        (orderedProduct: ProductView | undefined) => {
            if (!orderedProduct) {
                return;
            }

            setOrderedProduct(orderedProduct);

            updateProductQueryData({
                queryKey: productsKey.list({ projectId: project?.id }),
                updater: oldData => {
                    if (!oldData) {
                        return { total: 1, data: [orderedProduct], page: 0 };
                    }

                    return {
                        ...oldData,
                        data: [orderedProduct, ...oldData.data],
                        list: [orderedProduct, ...oldData.data],
                    };
                },
            });

            setIsOrdered(true);
            trackEvent(GAEvents.SERVICE_CREATE, { catalogId: catalog.id });
        },
        error => {
            const axiosError = error as AxiosError<string>;

            const isCodeIncluded = axiosError?.response?.data.includes('code');

            if (isCodeIncluded) {
                toast({ description: t('serviceRequest.codeFormatErrorMsgText') });
                return;
            }

            toast({ description: axiosError?.response?.data });
        }
    );

    const isSubmitBtnDisabled = orderCatalogs.isPending || !agree;

    const onSubmit = () => {
        orderCatalogs.mutateAsync({
            projectId: project?.id as string,
            catalogId: catalog?.id as string,
            memo: watchedMemo,
            medias: medias.map(({ id, ...rest }) => rest),
            catalogs: [],
        });
    };

    const handleClickDetail = () => {
        if (!orderedProduct || !orderedProduct.id) {
            return;
        }

        onOpenChange(false);
        if (!workspaceFromServer || !myProject) {
            toast({
                description: 'Unable to access workspace or project. Please check your permissions and try again.',
                variant: 'destructive',
            });
            navigate('/w', { replace: true });
            return;
        }

        navigate(`/w/${workspaceFromServer.id}/p/${myProject.id}/products/${orderedProduct.id}`, { replace: true });
    };

    const handleOpenChange = (open: boolean) => {
        onOpenChange(open);
        if (!open) {
            setIsOrdered(false);
            setOrderedProduct(null);
            resetUploadedFiles();
            setMedias([]);
        }
    };

    const fileBtnTapped = () => {
        inputRef.current?.click();
    };

    const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target;

        const fileList = input.files;
        const fileLength = fileList?.length || 0;

        const maxLength = 5 - medias.length;

        if (!fileLength || fileLength > maxLength) {
            return;
        }

        uploadFiles(fileList as FileList);
    };

    const fileDeleteBtnTapped = (mediaId: string | undefined) => {
        if (!mediaId) {
            return;
        }
        setMedias(prev => prev.filter(media => media.id !== mediaId));
    };

    const getIconImage = (catalog: CatalogView | undefined) => {
        if (!catalog) {
            return '/assets/icons/empty.svg';
        }

        const thumb = catalog.thumbs && catalog.thumbs.length >= 1 ? catalog.thumbs[0] : null;

        if (thumb) {
            return thumb;
        }

        return getStereoIcon(catalog.stereo as string);
    };

    useEffect(() => {
        if (!uploadedFilesData) {
            return;
        }
        setMedias(prev => [...prev, ...uploadedFilesData]);
    }, [uploadedFilesData]);

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent aria-describedby={undefined} className="p-0">
                {!isOrdered && (
                    <>
                        <DialogHeader>
                            <DialogTitle>{t('serviceRequest.title')}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={productOrderForm.handleSubmit(onSubmit)}>
                            <div className="p-6 overflow-auto">
                                <div className="text-[20px] whitespace-pre-wrap text-text font-medium">
                                    {t('serviceRequest.sub')}
                                </div>
                                <div className="w-full h-[1px] bg-gray-200 my-6"></div>
                                <div className="text-[18px] font-medium mb-[6px]">
                                    {t('serviceRequest.serviceToApply')}
                                </div>
                                <div className="flex items-center gap-2 mt-6 mb-3">
                                    <Avatar className="w-[50px] h-[50px]">
                                        <AvatarImage src={getIconImage(catalog as CatalogView)} alt={catalog.stereo} />
                                    </Avatar>
                                    <div className="flex flex-col flex-1">
                                        <Badge className="text-sm text-gray-800 capitalize self-start h-[25px]">
                                            {t(catalog?.isDomestic ? 'serviceDetail.domestic' : 'serviceDetail.global')}
                                        </Badge>
                                        <div className="text-[18px] font-medium">
                                            {(i18n.language === 'en' && catalog.nameEn) || catalog.name}
                                        </div>
                                    </div>
                                </div>
                                <div className="w-full h-[1px] bg-gray-200 my-6"></div>
                                <Label className="text-[18px] mb-4 font-medium">
                                    {t('serviceRequest.paymentInfo')}
                                </Label>
                                <div className="space-y-[17px]">
                                    <div className="flex justify-between items-center font-medium">
                                        <div className="text-base font-medium text-gray-700">
                                            {t('price.service.monthly_subscription_fee')}
                                        </div>
                                        <span className="text-lg font-semibold text-gray-black">
                                            {getCurrencySymbol(catalog?.price$?.currency)}
                                            {
                                                // TODO:
                                                '-'
                                            }
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center font-medium">
                                        <div className="text-base font-medium text-gray-700">
                                            {t('price.service.initial_setup_fee')}
                                        </div>
                                        <span className="text-lg font-semibold text-gray-black">
                                            {getCurrencySymbol(catalog?.price$?.currency)}
                                            {catalog?.price$?.initPrice}
                                        </span>
                                    </div>

                                    <div className="flex justify-between items-center font-medium">
                                        <div className="text-base font-medium text-gray-700">
                                            {t('price.service.usage_fee')}
                                        </div>
                                        <span className="text-lg font-semibold text-gray-black">
                                            {getCurrencySymbol(catalog?.price$?.currency)}
                                            {
                                                // TODO:
                                                '-'
                                            }
                                        </span>
                                    </div>
                                </div>
                                <div className="bg-gray-50 py-[6px] px-2 rounded-[11px] space-y-[2px] mt-[9px]">
                                    <div className="flex justify-between items-center font-medium">
                                        <div className="text-[15px] text-[#357AFF] font-medium">
                                            {t('price.service.traffic_cost')}
                                        </div>
                                        <span className="text-base text-gray-800 font-semibold">
                                            {getCurrencySymbol(catalog?.price$?.currency)}
                                            {catalog?.price$?.trafficPrice}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center font-medium">
                                        <div className="text-[15px] text-[#357AFF] font-medium">
                                            {t('price.service.cost_per_hour')}
                                        </div>
                                        <span className="text-base text-gray-800 font-semibold">
                                            {getCurrencySymbol(catalog?.price$?.currency)}
                                            {catalog?.price$?.hourPerPrice}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center font-medium">
                                        <div className="text-[15px] text-[#357AFF] font-medium">
                                            {t('price.service.per_call')}
                                        </div>
                                        <span className="text-base text-gray-800 font-semibold">
                                            {getCurrencySymbol(catalog?.price$?.currency)}
                                            {catalog?.price$?.callPerPrice}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="text-sm font-medium flex items-center justify-between mt-1 mb-7 px-6">
                                <button
                                    type="button"
                                    onClick={() => setAgree(prev => !prev)}
                                    className="inline-flex items-center gap-[6px]"
                                >
                                    <div className="w-4 h-4 border-[1.5px] border-primary rounded-full flex items-center justify-center">
                                        {agree && <div className="w-2 h-2 rounded-full bg-primary"></div>}
                                    </div>
                                    <label htmlFor="1" className="text-sm text-sub cursor-pointer">
                                        {t('workspace.create.termsOfServiceAgreement')}({t('workspace.create.required')}
                                        )
                                    </label>
                                </button>
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <button
                                            className="text-sm text-gray-600 underline hover:text-gray-900"
                                            type="button"
                                        >
                                            {t('workspace.create.view')}
                                        </button>
                                    </DialogTrigger>
                                    <DialogPortal>
                                        <DialogContent
                                            ref={scrollDivRef}
                                            className="max-md:max-w-full max-md:w-full max-md:max-h-[100vh] max-w-[800px] max-md:rounded-none p-0"
                                        >
                                            <DialogHeader>
                                                <DialogTitle>Privacy & Terms</DialogTitle>
                                            </DialogHeader>
                                            <PrivacyTermsPage isDialog={true} />
                                        </DialogContent>
                                    </DialogPortal>
                                </Dialog>
                            </div>
                            {catalog.isDomestic && (
                                <>
                                    <div className="w-full h-[1px] bg-gray-200 my-6"></div>
                                    <div className="text-[18px] font-medium mb-3">
                                        {t('serviceRequest.additionalInfo')}
                                    </div>
                                    <div className="flex flex-col gap-y-6">
                                        <div className="flex flex-col gap-y-1">
                                            <Label>{t('serviceRequest.contact')}</Label>
                                            <Controller
                                                name="memo"
                                                control={productOrderForm.control}
                                                rules={{
                                                    required: '메모를 입력해주세요.',
                                                }}
                                                render={({ field, fieldState }) => (
                                                    <div className="w-full">
                                                        <Input
                                                            {...field}
                                                            placeholder={t('project.create.placeholder')}
                                                        />
                                                        {fieldState.error && (
                                                            <p className="mt-1 text-xs text-red-500">
                                                                {fieldState.error.message}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                        <div className="flex flex-col">
                                            <Label className="text-base font-semibold">
                                                {t('serviceRequest.addFile')}
                                            </Label>
                                            <div className="flex gap-x-2 overflow-auto">
                                                <button
                                                    type="button"
                                                    disabled={isFileBtnDisabled}
                                                    onClick={fileBtnTapped}
                                                    className="flex-shrink-0 w-[72px] h-[72px] border border-text-400 border-dashed rounded-[10px] flex flex-col gap-y-[2px] items-center justify-center"
                                                >
                                                    {isUploading ? (
                                                        <Spinner className="w-4 h-4" />
                                                    ) : (
                                                        <>
                                                            <File strokeWidth={1.5} className="text-sub w-7 h-7" />
                                                            <div className="text-xs text-sub">
                                                                {medias.length > 0 ? (
                                                                    <span>{medias.length}</span>
                                                                ) : (
                                                                    medias.length
                                                                )}
                                                                <span className="text-text-400">/5</span>
                                                            </div>
                                                        </>
                                                    )}
                                                </button>
                                                <input
                                                    type="file"
                                                    multiple
                                                    className="hidden"
                                                    ref={inputRef}
                                                    onChange={handleFileInputChange}
                                                />

                                                {medias.map(media => (
                                                    <div key={media.id} className="relative">
                                                        <button
                                                            type="button"
                                                            onClick={() => fileDeleteBtnTapped(media.id)}
                                                            className="absolute top-1 right-1 w-4 h-4 bg-[#EAEAEC] flex justify-center items-center rounded-full"
                                                        >
                                                            <X className="w-3 h-3 text-black" />
                                                        </button>
                                                        {media.contentType?.includes('pdf') ? (
                                                            <PdfItem fileUrl={media.url} />
                                                        ) : (
                                                            <ImageItem fileUrl={media.url} />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                            <DialogFooter className="px-[18px]">
                                <Button size="lg" className="w-full" disabled={isSubmitBtnDisabled}>
                                    {orderCatalogs.isPending ? <SharedSpinner /> : t('serviceRequest.makePayment')}
                                </Button>
                            </DialogFooter>
                        </form>
                    </>
                )}
                {isOrdered && (
                    <>
                        <DialogHeader></DialogHeader>
                        <div className="px-6 pt-5 pb-6">
                            <div className="flex justify-center items-center">
                                <Image src={CompleteImage} className="w-20 h-20" />
                            </div>
                            <DialogTitle className="text-[20px] text-text font-medium mt-4 mb-8">
                                {t('serviceComplete.title')}
                            </DialogTitle>
                            <div className="text-[18px] font-medium mb-3">{t('serviceComplete.payInfo')}</div>

                            {orderedProduct && (
                                <ul className="flex flex-col gap-2 py-3 px-[19px] rounded-xl !border-[0.6px] border-gradient">
                                    <li className="flex items-center justify-between py-3 border-b border-gray-200">
                                        <div className="text-[15px] font-medium text-gray-600">
                                            {t('serviceComplete.serviceName')}
                                        </div>
                                        <b className="font-semibold text-gray-900">{orderedProduct?.name}</b>
                                    </li>
                                    <li className="flex items-center justify-between  py-3 border-b border-gray-200">
                                        <div className="text-[15px] font-medium text-gray-600">
                                            {t('serviceComplete.paymentDate')}
                                        </div>
                                        <b className="font-semibold text-gray-900">
                                            {formatDate(orderedProduct.createdAt)}
                                        </b>
                                    </li>
                                    <li className="flex items-center justify-between py-3">
                                        <div className="text-[15px] font-medium text-gray-600">
                                            {t('serviceReceipt.status')}
                                        </div>
                                        <b className="text-[18px] font-semibold text-gray-900">
                                            {t(`project.${orderedProduct.state}`)}
                                        </b>
                                    </li>
                                </ul>
                            )}

                            <div className="mt-9 mb-4 flex flex-col gap-[10px] items-center">
                                <Button size="lg" className="w-full" onClick={closeDialog}>
                                    {t('serviceComplete.confirm')}
                                </Button>
                                <button
                                    className="flex items-center text-sm text-sub font-medium"
                                    onClick={handleClickDetail}
                                    type="button"
                                >
                                    {t('serviceComplete.viewApplicationDetails')}
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
